import * as joint from '@clientio/rappid';
import { LegacySidebarService } from 'guideline/feature-sidebar';

import { FlowchartLinkDto } from '@treetop/shared/model';

import { LinkAdjustToolEvent } from './LinkAdjustToolEvent';
import { LinkVerticeCalculator } from './LinkVerticeCalculator';
import { LinkVerticeCalculatorLimited } from './LinkVerticeCalculatorLimited';
import { FlowchartElementsSubjectStore } from '../../../data/flowchart-elements-subject-store.service';
import { FlowchartLoaderService } from '../../flowchart-loader.service';
import { CustomRouter } from '../custom-router/CustomRouter';

export class LinkToolGenerator {
  public static getLinkAdjusterTool(
    flowchartElementsSubjectStore: FlowchartElementsSubjectStore,
    flowchartLoaderService: FlowchartLoaderService,
    graph: joint.dia.Graph,
    sidebarService: LegacySidebarService,
  ): joint.linkTools.Button {
    return new joint.linkTools.Button({
      markup: [
        {
          className: 'custom-link-adjuster',
          tagName: 'circle',
          selector: 'button',
          attributes: {
            r: 7,
            fill: '#000000',
            cursor: 'move',
            class: 'custom-link-adjuster',
          },
        },
      ],
      distance: 10,
      offset: 0,
      action: function (buttonEvent, linkView, buttonView) {
        linkView.hideTools();
        linkView.model.set('manual', true);
        flowchartLoaderService.setManualLink(flowchartElementsSubjectStore, linkView.model.attributes.id, true);
        linkView.model.set('editVertices', true);
        linkView.updateTools;

        const eventRequirements = LinkAdjustToolEvent.getEventRequirements(buttonEvent, buttonView, linkView);
        let calculatorInstance = new LinkVerticeCalculator();
        let subflowPosition: joint.g.PlainPoint;
        let subflowSize: joint.dia.Size;
        const sourceElement = graph.getCell(linkView.model.attributes.source.id);
        if (sourceElement.attributes.subflowParentId) {
          calculatorInstance = new LinkVerticeCalculatorLimited();
          const subflow = graph.getCell(sourceElement.attributes.subflowParentId);
          subflowPosition = subflow.attributes.position;
          subflowSize = subflow.attributes.size;
        }

        function handleMouseMove(mouseEvent: MouseEvent) {
          return LinkAdjustToolEvent.handleMouseMove(
            mouseEvent,
            buttonView,
            linkView,
            eventRequirements,
            calculatorInstance,
            subflowPosition,
            subflowSize,
          );
        }

        function handleMouseUp() {
          linkView.removeRedundantLinearVertices();
          const vertices = linkView.model.vertices();
          //@ts-ignore
          CustomRouter.checkRedundancy(vertices, linkView.sourcePoint as joint.g.PlainPoint);
          linkView.model.set('vertices', vertices);
          eventRequirements.element.vertices(vertices);

          const elements = flowchartElementsSubjectStore.getCloneDeepValue();

          const link: FlowchartLinkDto = sidebarService.deepSearchLinks(elements, linkView.model.id as string);

          if (link) {
            link.linkVertices = eventRequirements.element.vertices().map((el) => {
              return { positionX: el.x, positionY: el.y };
            });
            const component = graph.getCell(linkView.model.attributes.source.id);
            flowchartLoaderService.setSubjectLinkVertices(
              flowchartElementsSubjectStore,
              component,
              linkView.model.id as string,
              link.linkVertices,
            );
          }
          linkView.model.set('editVertices', false);
          // Remove event listeners to prevent false events and memory leaks!
          document.removeEventListener('mousemove', handleMouseMove);
          document.removeEventListener('mouseup', handleMouseUp);
        }

        // Add the initial event listeners in the main part of your code
        document.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('mousemove', handleMouseMove);
      },
    });
  }

  static getResetTool(
    flowchartElementsSubjectStore: FlowchartElementsSubjectStore,
    graph: joint.dia.Graph,
    flowchartLoaderService: FlowchartLoaderService,
  ): joint.linkTools.Button {
    return new joint.linkTools.Button({
      markup: [
        {
          tagName: 'circle',
          selector: 'button',
          attributes: {
            r: 12,
            fill: '#2196F3',
            cursor: 'pointer',
          },
        },
        {
          tagName: 'path',
          selector: 'icon',
          attributes: {
            d: 'M 3.534 -1 h 3.932 a 0.25 0.25 0 0 1 0.192 0.41 l -1.966 2.36 a 0.25 0.25 0 0 1 -0.384 0 l -1.966 -2.36 a 0.25 0.25 0 0 1 0.192 -0.41 z m -11 2 h 3.932 a 0.25 0.25 0 0 0 0.192 -0.41 L -5.308 -1.77 a 0.25 0.25 0 0 0 -0.384 0 L -7.658 0.59 A 0.25 0.25 0 0 0 -7.466 1 z',
            fill: 'none',
            stroke: '#FFFFFF',
            'stroke-width': 1,
            'pointer-events': 'none',
          },
        },
        {
          tagName: 'path',
          selector: 'subicon',
          attributes: {
            d: 'M 0 -5 c -1.552 0 -2.94 0.707 -3.857 1.818 a 0.5 0.5 0 1 1 -0.771 -0.636 A 6.002 6.002 0 0 1 5.917 -1 H 4.9 A 5.002 5.002 0 0 0 0 -5 z M -4.9 1 a 5.002 5.002 0 0 0 8.757 2.182 a 0.5 0.5 0 1 1 0.771 0.636 A 6.002 6.002 0 0 1 -5.917 1 H -4.9 z',
            fill: 'none',
            stroke: '#FFFFFF',
            'stroke-width': 1,
            'pointer-events': 'none',
          },
        },
      ],
      distance: 50,
      offset: -20,
      action: function (buttonEvent, linkView) {
        linkView.hideTools();
        linkView.model.set('manual', false);
        linkView.model.set('editVertices', false);
        flowchartLoaderService.setManualLink(flowchartElementsSubjectStore, linkView.model.attributes.id, false);
        linkView.model.router(function (vertices, args, linkView) {
          return CustomRouter.router(vertices, linkView);
        });
        flowchartLoaderService.setSubjectLinkVertices(
          flowchartElementsSubjectStore,
          graph.getCell(linkView.model.attributes.source.id),
          linkView.model.id as string,
          linkView.model.attributes.vertices,
        );
      },
    });
  }
}
